import { StrictMode, useRef } from 'react';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { environment } from 'env/cashier';

import { CashierClient } from '@cashier/cashier-data-access';
import {
  convertToLocale,
  createCombinedFeatureStore,
  generateAuthorizationToken,
  useCashierHeightObserver,
} from '@cashier/cashier-shared';
import { CommonFeature } from '@cashier/feature/common';
import { PaymentFormFeature } from '@cashier/feature/payment-form';
import { PaymentProcessingFeature } from '@cashier/feature/payment-processing';
import { PaymentResultFeature } from '@cashier/feature/payment-result';

import { getAppRouter } from './elements/routes';

const params = new URLSearchParams(window.location.search);

const merchantId = params.get('merchantId');
const userId = params.get('userId');
const sessionId = params.get('sessionId');
const language = params.get('language');
const apiVersions = params.get('apiVersions');

const cashierClient = new CashierClient(
  environment.paymentGatewayApiUrl,
  environment.directa24DropdownUrl,
  environment.mifinityDropdownUrl,
  convertToLocale(language),
  generateAuthorizationToken({ merchantId, userId, sessionId }),
  apiVersions || 'v1',
);

const store = createCombinedFeatureStore(
  [
    PaymentFormFeature.store,
    PaymentProcessingFeature.store,
    PaymentResultFeature.store,
    CommonFeature.store,
  ],
  {
    dependencies: { Api: cashierClient },
  },
);

export const App = () => {
  const appContainerRef = useRef<HTMLDivElement>(null);
  useCashierHeightObserver(appContainerRef);

  return (
    <StrictMode>
      <Provider store={store}>
        <div ref={appContainerRef}>
          <RouterProvider
            router={getAppRouter(store, {
              setClientVersion: (version) =>
                cashierClient.setVersionHeader(version),
            })}
          />
        </div>
      </Provider>
    </StrictMode>
  );
};
